// POW (Proof of Work) computation functions

export interface POWChallengeData {
  c: number; // 计算次数
  s: number; // salt长度
  d: number; // 难度
}

export interface POWChallengeResponse {
  challenge: POWChallengeData;
  token: string;
  expires: number;
}

// FNV-1a 哈希算法实现（32位）
function fnv1a(str: string): number {
  let hash = 2166136261;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    
    // 计算所有移位（使用异或后的hash值）
    const shift1 = (hash << 1) & 0xffffffff;
    const shift4 = (hash << 4) & 0xffffffff;
    const shift7 = (hash << 7) & 0xffffffff;
    const shift8 = (hash << 8) & 0xffffffff;
    const shift24 = (hash << 24) & 0xffffffff;
    
    // 将所有移位结果相加
    hash = (hash + shift1 + shift4 + shift7 + shift8 + shift24) & 0xffffffff;
  }
  return hash >>> 0; // 确保是无符号32位整数
}

// 模拟JavaScript的无符号右移操作（匹配Go实现）
function unsignedRightShift(value: number, shift: number): number {
  if (shift < 0 || shift > 31) {
    return 0;
  }
  // JavaScript的>>>已经是无符号右移，但为了完全匹配Go的实现逻辑
  // Go版本对最高位有特殊处理，但JS的>>>已经正确处理了
  return value >>> shift;
}

// 从字符串种子生成指定长度的确定性十六进制字符串
function prng(seed: string, length: number): string {
  let state = fnv1a(seed);
  let result = '';

  while (result.length < length) {
    // Xorshift算法（完全匹配Go实现）
    state ^= (state << 13) & 0xffffffff;
    const urShift17 = unsignedRightShift(state, 17);
    state ^= urShift17;
    state ^= (state << 5) & 0xffffffff;
    state &= 0xffffffff;

    // 转换为十六进制字符串（匹配Go的%08x格式）
    const hexStr = (state >>> 0).toString(16).padStart(8, '0');
    result += hexStr;
  }

  if (result.length > length) {
    return result.substring(0, length);
  }
  return result;
}

// 计算SHA256哈希
async function sha256(input: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// 计算单个POW解决方案
async function computeSingleSolution(
  token: string,
  index: number,
  saltLength: number,
  difficulty: number
): Promise<number> {
  // 生成salt（使用token + 索引）
  const salt = prng(token + index.toString(), saltLength);

  // 生成target（使用token + 索引 + 'd'）
  const target = prng(token + index.toString() + 'd', difficulty);

  // 暴力搜索解决方案
  // 从0开始，找到一个solution使得 SHA256(salt + solution) 以target开头
  for (let solution = 0; solution < 10000000; solution++) {
    const hashInput = salt + solution.toString();
    const hash = await sha256(hashInput);

    if (hash.length >= target.length && hash.substring(0, target.length) === target) {
      return solution;
    }
  }

  throw new Error(`Failed to find solution for index ${index}`);
}

/**
 * 计算POW解决方案（带进度回调）
 * @param challenge POW挑战数据
 * @param token 挑战token
 * @param onProgress 进度回调函数 (current, total) => void
 * @returns Promise<number[]> 解决方案数组
 */
export async function computePOWSolutionsWithProgress(
  challenge: POWChallengeData,
  token: string,
  onProgress?: (current: number, total: number) => void
): Promise<number[]> {
  const solutions: number[] = [];
  const total = challenge.c;

  for (let i = 1; i <= total; i++) {
    const solution = await computeSingleSolution(
      token,
      i,
      challenge.s,
      challenge.d
    );
    solutions.push(solution);

    // 调用进度回调
    if (onProgress) {
      onProgress(i, total);
    }
  }

  return solutions;
}
